import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import SignupStepHeader from './SignupStepHeader';
import MsspContactStep from './steps/MsspContactStep';
import MsspCompanyStep from './steps/MsspCompanyStep';
import { MSSP_SIGNUP_STEPS, getStepByKey, getNextStep, getPreviousStep } from './msspSignupStepConfig';
import LoadingSpinner from '../LoadingSpinner';
import { useGoogleMapsLoader } from '../../hooks/useGoogleMapsLoader';
import { defaultRegionId } from '../../utils/regions';

// ── CAPTCHA ──────────────────────────────────────────────────────────────────
// Unambiguous alphanumeric characters — excludes visually similar glyphs:
// 0/O, 1/I/l, 5/S, 8/B, Z/2.
const CAPTCHA_CHARSET = 'ABCDEFGHJKLMNPQRTUVWXY346789';
const CAPTCHA_LENGTH  = 6;

const CAPTCHA_CHAR_COLORS = [
  '#1a3a5c', '#243068', '#1e3c62', '#2a3870', '#163254', '#1c2e58',
];

const buildCharData = () =>
  Array.from({ length: CAPTCHA_LENGTH }, (_, i) => ({
    rotation : (Math.random() - 0.5) * 38,
    yOffset  : (Math.random() - 0.5) * 14,
    fontSize : 21 + Math.random() * 7,
    colorIdx : i % CAPTCHA_CHAR_COLORS.length,
  }));

const buildNoiseLines = () =>
  Array.from({ length: 5 }, () => ({
    x1: Math.random() * 240, y1: Math.random() * 72,
    x2: Math.random() * 240, y2: Math.random() * 72,
  }));

const generateCaptcha = () => {
  let chars = '';
  for (let i = 0; i < CAPTCHA_LENGTH; i++) {
    chars += CAPTCHA_CHARSET[Math.floor(Math.random() * CAPTCHA_CHARSET.length)];
  }
  return {
    captchaQuestion  : chars,
    captchaExpected  : chars.toUpperCase(),
    captchaCharData  : buildCharData(),
    captchaNoiseLines: buildNoiseLines(),
    captchaAnswer    : '',
  };
};

// ── Field-name adapter ───────────────────────────────────────────────────────
// useAddressAutocomplete fires onBatchChange with organization* keys because it
// was written for the standard signup form.  This map translates them to the
// MSSP field names before they reach formData.
const ORG_TO_MSSP = {
  organizationAddress1: 'address1',
  organizationCity:     'city',
  organizationState:    'state',
  organizationZip:      'postalCode',
  organizationCountry:  'country',
};

const MsspSignup = () => {
  const navigate = useNavigate();

  // Pre-load Maps script so it is ready when the user reaches the company step.
  useGoogleMapsLoader();

  // Company step is first
  const [currentStepKey, setCurrentStepKey] = useState('company');

  const [formData, setFormData] = useState(() => ({
    // Step 1: Company Info
    regionId:     '',
    msspName:     '',
    address1:     '',
    city:         '',
    state:        '',
    postalCode:   '',
    country:      '',
    referralSource: '',
    // Step 2: Contact Info
    userFirstName:   '',
    userLastName:    '',
    userDomain:      '',
    userEmail:       '',
    userPhoneNumber: '',
    // CAPTCHA — generated once on mount
    ...generateCaptcha(),
  }));

  const [errors,      setErrors]      = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [validating,  setValidating]  = useState(false);

  // Regions for the dropdown (fetched on mount)
  const [regions,     setRegions]     = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError,   setDataError]   = useState(null);

  // Fetch regions on mount (public endpoint — no auth required)
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/Regions`);
        const loadedRegions = res.data ?? [];
        setRegions(loadedRegions);
        // Only configured regions are listed; with one, the picker is hidden and it is used.
        setFormData(prev => ({ ...prev, regionId: defaultRegionId(loadedRegions, prev.regionId) }));
      } catch (err) {
        setDataError('Failed to load form data. Please refresh the page.');
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, []);

  // Ref for the async address validator registered by MsspCompanyStep
  const companyStepAsyncValidateRef = useRef(null);

  // ── Field change handler ────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'regionId') {
      // Changing region resets country (options change) and state (depends on country)
      setFormData(prev => ({ ...prev, regionId: value }));
    } else if (name === 'country') {
      // Clear state when country changes (state options differ per country)
      setFormData(prev => ({ ...prev, country: value, state: '' }));
    } else if (name === 'postalCode' || name === 'city' || name === 'state') {
      // User manually edited a location field — previously autocompleted
      // address is no longer consistent, so clear it.
      setFormData(prev => ({ ...prev, [name]: value, address1: '' }));
    } else if (name === 'userDomain') {
      // Domain changed — clear email so user re-enters one matching the new domain
      setFormData(prev => ({ ...prev, userDomain: value, userEmail: '' }));
      setErrors(prev => ({ ...prev, userDomain: null, userEmail: null }));
      return;
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // ── Batch change adapter ────────────────────────────────────────────────
  // Receives organization* keys from useAddressAutocomplete and translates
  // them to MSSP field names before writing to formData.
  const handleBatchChange = useCallback((fields) => {
    setFormData(prev => {
      const updates = {};
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined) updates[ORG_TO_MSSP[k] ?? k] = v;
      });
      return { ...prev, ...updates };
    });
    setErrors(prev => {
      const cleared = { ...prev };
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined) cleared[ORG_TO_MSSP[k] ?? k] = null;
      });
      return cleared;
    });
  }, []);

  // ── Email domain blur check ──────────────────────────────────────────────
  // Fires when the user leaves the email field — avoids alerts during typing.
  const handleEmailBlur = (e) => {
    const value = e.target.value.trim();
    const atIdx = value.indexOf('@');
    if (atIdx === -1) return; // incomplete — let required validation handle it
    const emailDomain = value.slice(atIdx + 1).toLowerCase();
    const orgDomain   = (formData.userDomain || '').toLowerCase().trim();
    if (emailDomain && orgDomain && emailDomain !== orgDomain) {
      alert(`Email must belong to the domain "${formData.userDomain}". Please enter a valid address.`);
      setFormData(prev => ({ ...prev, userEmail: '' }));
      setErrors(prev => ({ ...prev, userEmail: null }));
    }
  };

  // ── CAPTCHA refresh ──────────────────────────────────────────────────────
  const handleRefreshCaptcha = () => {
    setFormData(prev => ({ ...prev, ...generateCaptcha() }));
    setErrors(prev => ({ ...prev, captchaAnswer: null }));
  };

  // ── Navigation ──────────────────────────────────────────────────────────
  const handleNext = async () => {
    const currentStep = getStepByKey(currentStepKey);

    const syncValidation = currentStep.validate(formData);
    if (!syncValidation.isValid) {
      setErrors(syncValidation.errors);
      return;
    }

    // Run async address validation on the company step
    if (currentStepKey === 'company' && companyStepAsyncValidateRef.current) {
      setValidating(true);
      try {
        const asyncResult = await companyStepAsyncValidateRef.current(formData);
        if (!asyncResult.isValid) {
          setErrors(asyncResult.errors);
          return;
        }
      } finally {
        setValidating(false);
      }
    }

    const nextStep = getNextStep(currentStepKey);
    if (nextStep) {
      setCurrentStepKey(nextStep.key);
      setErrors({});
    }
  };

  const handleBack = () => {
    const prevStep = getPreviousStep(currentStepKey);
    if (prevStep) {
      setCurrentStepKey(prevStep.key);
      setErrors({});
    }
  };

  // ── Submission ──────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const currentStep = getStepByKey(currentStepKey);
    const validation  = currentStep.validate(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Check that the email domain is not a public/consumer domain
    if (formData.userEmail) {
      const atIdx = formData.userEmail.indexOf('@');
      const emailDomain = atIdx !== -1 ? formData.userEmail.slice(atIdx + 1).toLowerCase().trim() : '';
      if (emailDomain) {
        try {
          const res = await axios.get(`${API_BASE_URL}/signup/check-domain?domain=${encodeURIComponent(emailDomain)}`);
          if (res.data.isPublic) {
            setErrors(prev => ({
              ...prev,
              userEmail: 'Public email domains are not allowed. Please use your business email address.'
            }));
            return;
          }
        } catch {
          // If check fails, allow the user to proceed (fail-open)
        }
      }
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const signupDto = {
        msspName:        formData.msspName,
        userFirstName:   formData.userFirstName,
        userLastName:    formData.userLastName,
        userDomain:      formData.userDomain      || null,
        userEmail:       formData.userEmail,
        userPhoneNumber: formData.userPhoneNumber,
        regionId:        formData.regionId ? Number(formData.regionId) : null,
        address1:        formData.address1,
        city:            formData.city            || null,
        state:           formData.state           || null,
        postalCode:      formData.postalCode      || null,
        country:         formData.country,
        referralSource:  formData.referralSource  || null,
      };

      await axios.post(`${API_BASE_URL}/Signup/mssp`, signupDto);
      navigate('/signup/confirmation', { state: { signupSuccess: true } });
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isFirstStep = !getPreviousStep(currentStepKey);
  const isLastStep  = !getNextStep(currentStepKey);

  // ── Loading / error gates ────────────────────────────────────────────────
  if (dataLoading) {
    return (
      <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
        <div className="bg-white p-10 rounded-[20px] shadow-[0_2px_16px_rgba(0,0,0,0.10)] text-center max-w-sm w-full">
          <p className="text-[#1d1d1f] font-medium mb-2">Unable to load</p>
          <p className="text-[#6e6e73] text-sm mb-5">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-[#0071e3] text-sm font-medium hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ── Step renderer ────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStepKey) {
      case 'company':
        return (
          <MsspCompanyStep
            formData={formData}
            onChange={handleChange}
            onBatchChange={handleBatchChange}
            errors={errors}
            regions={regions}
            onRegisterAsyncValidate={(fn) => { companyStepAsyncValidateRef.current = fn; }}
          />
        );
      case 'contact':
        return (
          <MsspContactStep
            formData={formData}
            onChange={handleChange}
            onEmailBlur={handleEmailBlur}
            onRefreshCaptcha={handleRefreshCaptcha}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center px-4 py-10">
      <div className="bg-white w-full max-w-[560px] rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.09)] relative">

        {/* Close button */}
        <button
          type="button"
          onClick={() => { window.location.href = '/'; }}
          className="absolute top-4 right-4 text-[#86868b] hover:text-[#1d1d1f] transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f5f7]"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Branding header */}
        <div className="pt-10 pb-7 px-10 text-center">
          <div className="inline-flex items-center justify-center w-11 h-11 bg-[#0071e3] rounded-[14px] mb-4 shadow-[0_4px_14px_rgba(0,113,227,0.35)]">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-[22px] font-semibold text-[#1d1d1f] tracking-[-0.3px]">
            MSSP Registration
          </h1>
          <p className="text-[14px] text-[#6e6e73] mt-1">
            Register as a Managed Security Service Provider
          </p>
        </div>

        {/* Step indicator */}
        <div className="px-10 pb-6">
          <SignupStepHeader steps={MSSP_SIGNUP_STEPS} currentStepKey={currentStepKey} />
        </div>

        {/* Divider */}
        <div className="h-px bg-[#f0f0f0]" />

        {/* Form content */}
        <div className="px-10 py-7">
          {renderStep()}
        </div>

        {/* Submit error */}
        {submitError && (
          <div className="mx-10 -mt-2 mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
            <p className="text-red-600 text-sm text-center">{submitError}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="px-10 pb-10">
          <div className={`grid gap-3 ${isFirstStep ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {!isFirstStep && (
              <button
                type="button"
                onClick={handleBack}
                className="py-3 rounded-xl text-[#1d1d1f] text-sm font-medium bg-[#f5f5f7] hover:bg-[#e8e8ed] transition-colors"
              >
                Back
              </button>
            )}

            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="py-3 rounded-xl text-white text-[14px] font-medium bg-[#0071e3] hover:bg-[#0077ed] active:bg-[#006edb] disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={validating}
                className="py-3 rounded-xl text-white text-[14px] font-medium bg-[#0071e3] hover:bg-[#0077ed] active:bg-[#006edb] disabled:opacity-50 transition-colors"
              >
                {validating ? 'Validating…' : 'Continue'}
              </button>
            )}
          </div>

          {/* Links */}
          <div className="mt-5 space-y-1.5 text-center">
            <p className="text-[13px] text-[#6e6e73]">
              Not an MSSP?{' '}
              <Link to="/signup" className="text-[#0071e3] hover:underline font-medium">
                Standard sign up
              </Link>
            </p>
            <p className="text-[13px] text-[#6e6e73]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0071e3] hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MsspSignup;
